/* eslint-disable @typescript-eslint/no-explicit-any */
import { generateClient } from "aws-amplify/data";
import { getCurrentUser } from "aws-amplify/auth";
import type { Schema } from "@backend-schema";

export interface OutdatedShare {
  id: string;
  recipientEmail: string;
  lastUpdated: string;
  sourceLastUpdated: string;
  daysBehind: number;
}

export async function getOutdatedShares(): Promise<OutdatedShare[]> {
  const client = generateClient<Schema>();
  const user = await getCurrentUser();

  const shares = await client.models.ShareInvitation.list({
    filter: {
      owner: { eq: user.username },
      status: { eq: "accepted" },
    },
  });

  const outdated: OutdatedShare[] = [];

  for (const share of shares.data) {
    if (share.lastUpdated && share.sourceLastUpdated) {
      const lastUpdated = new Date(share.lastUpdated);
      const sourceUpdated = new Date(share.sourceLastUpdated);

      if (sourceUpdated > lastUpdated) {
        const daysBehind = Math.floor(
          (sourceUpdated.getTime() - lastUpdated.getTime()) /
            (1000 * 60 * 60 * 24),
        );
        outdated.push({
          id: share.id ?? "",
          recipientEmail: share.recipientEmail ?? "",
          lastUpdated: share.lastUpdated ?? "",
          sourceLastUpdated: share.sourceLastUpdated ?? "",
          daysBehind,
        });
      }
    }
  }

  return outdated;
}

export async function updateSharesWithCurrentData(
  shareIds: string[],
): Promise<void> {
  const client = generateClient<Schema>();
  await getCurrentUser();
  const now = new Date().toISOString();

  // Get current skill data
  const [skillRatings, certifications, userSkillPackages, customSkillPackages] =
    await Promise.all([
      client.models.SkillRating.list(),
      client.models.Certifications.list(),
      client.models.UserSkillPackages.list(),
      client.models.CustomSkillPackages.list(),
    ]);

  const [, , ,] = await Promise.all([
    client.models.SkillCategories.list(),
    client.models.Skills.list(),
    client.models.CustomSkillCategories.list(),
    client.models.CustomSkills.list(),
  ]);

  // Get list of current package IDs that user still has
  const currentPackageIds = new Set([
    ...userSkillPackages.data
      .map((up) => up.skillPackageId)
      .filter((id): id is string => id != null),
    ...customSkillPackages.data
      .map((cp) => cp.id)
      .filter((id): id is string => id != null),
  ]);

  // Get list of current certification IDs that user still has
  const currentCertIds = new Set(certifications.data.map((c) => c.id));

  // For each share, update only the skills that were originally shared
  const updatePromises = shareIds.map(async (shareId) => {
    const share = await client.models.ShareInvitation.get({ id: shareId });
    if (!share?.data?.sharedData) return;

    try {
      const originalSharedData = JSON.parse(share.data.sharedData);

      // Filter out packages that no longer exist in user's profile
      const updatedPackages =
        originalSharedData.skillPackages
          ?.filter((pkg: any) => currentPackageIds.has(pkg.id))
          .map((pkg: any) => {
            const updatedCategories = pkg.categories?.map((cat: any) => {
              const updatedSkills = cat.skills?.map((skill: any) => {
                // Find current rating for this skill
                const currentRating = skillRatings.data.find(
                  (r) => r.skillId === skill.id || r.skillKey === skill.name,
                );

                return {
                  ...skill,
                  rating: currentRating?.rating || skill.rating,
                };
              });

              return {
                ...cat,
                skills: updatedSkills,
              };
            });

            return {
              ...pkg,
              categories: updatedCategories,
            };
          }) || [];

      // Filter out certifications that no longer exist
      const updatedCertifications =
        originalSharedData.certifications?.filter((cert: any) =>
          currentCertIds.has(cert.id),
        ) || [];

      const updatedSharedData = {
        ...originalSharedData,
        skillPackages: updatedPackages,
        certifications: updatedCertifications,
      };

      return client.models.ShareInvitation.update({
        id: shareId,
        sharedData: JSON.stringify(updatedSharedData),
        lastUpdated: now,
        sourceLastUpdated: now,
        version: (share.data.version || 1) + 1,
      } as any);
    } catch (error) {
      console.error(`Failed to update share ${shareId}:`, error);
      return null;
    }
  });

  await Promise.all(updatePromises.filter(Boolean));
}

export async function markSkillPackageUpdated(): Promise<void> {
  const client = generateClient<Schema>();
  const user = await getCurrentUser();
  const now = new Date().toISOString();

  // Get all user's shares
  const shares = await client.models.ShareInvitation.list({
    filter: {
      owner: { eq: user.username },
    },
  });

  // Separate auto-update and manual shares
  const autoUpdateShares = shares.data.filter((share) => share.autoUpdate);
  const manualShares = shares.data.filter((share) => !share.autoUpdate);

  // Auto-update shares with current data
  if (autoUpdateShares.length > 0) {
    await updateSharesWithCurrentData(
      autoUpdateShares
        .map((share) => share.id)
        .filter((id): id is string => id !== null),
    );
  }

  // Mark manual shares as having source updates available
  const manualUpdatePromises = manualShares.map((share) =>
    client.models.ShareInvitation.update({
      id: share.id,
      sourceLastUpdated: now,
    } as any),
  );

  await Promise.all(manualUpdatePromises);
}
